import type { CriterionType, PerspectiveType } from "../types";
import { MIN_SEGMENT_COUNT, MAX_SEGMENT_COUNT, validateSegmentCount } from "./customerValue/scoringScaleValidator";

export type WorkspaceProcessStatus =
  | "department_input_open"
  | "hauffe_os_evaluation_ready"
  | "value_round_open"
  | "value_round_completed"
  | "clusters_ready_for_owner_review"
  | "weighting_open"
  | "calculation_ready_for_owner_review"
  | "calculation_confirmed";

export type DepartmentValueInputStatus = "closed" | "draft" | "submitted" | "open";
export type CustomerValueCriterionKind = "perspective" | "system_metric" | "prospect_metric";
export type CustomerValueScoringType = "manual_1_10" | "numeric_higher_better" | "numeric_lower_better" | "negative_effort";

export interface DepartmentValueInputForClustering {
  departmentId: string;
  departmentName: string;
  rawInput: string;
}

export interface SuggestedCustomerValueCluster {
  name: string;
  description: string;
  criterionType: CriterionType;
  criterionKind: CustomerValueCriterionKind;
  scoringType: CustomerValueScoringType;
  unit?: string;
  sourceTerms: string[];
  sourceDepartments: string[];
  suggestedWeight: number;
  sortOrder: number;
}

export interface NumericScalePoint {
  scoreValue: number;
  minValue?: number;
  maxValue?: number;
  label: string;
  unit?: string;
}

export interface SegmentDefinition {
  name: string;
  minScore: number;
  maxScore: number;
  description: string;
  meaning?: string;
  typicalCharacteristics?: string[];
  risks?: string[];
  recommendation?: string;
  relevantDepartments?: string[];
  crmDataHints?: string[];
}

export interface WeightValidationResult {
  ok: boolean;
  total: number;
  remaining: number;
  message: string;
}

export type WeightInputValue = number | string | null | undefined;
export type WorkspaceLanguage = "de" | "en";

type ClusterDefinition = {
  name: string;
  description: string;
  criterionType: CriterionType;
  criterionKind: CustomerValueCriterionKind;
  scoringType: CustomerValueScoringType;
  unit?: string;
  keywords: string[];
};

const clusterDefinitionsByLanguage: Record<WorkspaceLanguage, ClusterDefinition[]> = {
  de: [
  {
    name: "Wirtschaftlicher Beitrag",
    description: "Bewertet, ob ein Kunde messbaren wirtschaftlichen Beitrag, Marge und planbaren Umsatz erzeugt.",
    criterionType: "positive",
    criterionKind: "system_metric",
    scoringType: "numeric_higher_better",
    unit: "€",
    keywords: ["umsatz", "marge", "deckungsbeitrag", "profit", "profitabil", "budget", "zahlung", "wirtschaft", "clv"]
  },
  {
    name: "Strategische Passung",
    description: "Bewertet, ob ein Kunde zur Positionierung, Zielkundenlogik und langfristigen Entwicklung passt.",
    criterionType: "positive",
    criterionKind: "perspective",
    scoringType: "manual_1_10",
    keywords: ["strategie", "passung", "fit", "zielkunde", "branche", "positionierung", "wachstum", "referenz"]
  },
  {
    name: "Beziehungsqualität",
    description: "Bewertet Vertrauen, Zusammenarbeit, Verlässlichkeit und Klarheit in der Kundenbeziehung.",
    criterionType: "positive",
    criterionKind: "perspective",
    scoringType: "manual_1_10",
    keywords: ["vertrauen", "zusammenarbeit", "beziehung", "verlässlich", "kommunikation", "partnerschaft", "loyal"]
  },
  {
    name: "Operativer Aufwand",
    description: "Macht sichtbar, ob Betreuung, Sonderwünsche und Abstimmungsaufwand den erzeugten Wert binden.",
    criterionType: "negative",
    criterionKind: "system_metric",
    scoringType: "negative_effort",
    unit: "Stunden",
    keywords: ["aufwand", "sonderwunsch", "komplex", "reklamation", "support", "service", "ressource", "betreuung"]
  },
  {
    name: "Entscheidungsklarheit",
    description: "Bewertet, ob Entscheider, Prioritäten, Budget und nächste Schritte auf Kundenseite klar sind.",
    criterionType: "positive",
    criterionKind: "perspective",
    scoringType: "manual_1_10",
    keywords: ["entscheidung", "entscheider", "priorität", "klarheit", "prozess", "freigabe", "budgetklarheit"]
  },
  {
    name: "Wiederkehrendes Potenzial",
    description: "Bewertet, ob Bedarf, Nutzung und Entwicklung über eine einzelne Transaktion hinaus tragfähig sind.",
    criterionType: "positive",
    criterionKind: "perspective",
    scoringType: "manual_1_10",
    keywords: ["wiederkehr", "bindung", "laufzeit", "potenzial", "ausbau", "repeat", "retention", "skalier"]
  },
  {
    name: "Markt- und Markenwirkung",
    description: "Bewertet, ob ein Kunde Sichtbarkeit, Glaubwürdigkeit oder Marktlernen erzeugt.",
    criterionType: "positive",
    criterionKind: "perspective",
    scoringType: "manual_1_10",
    keywords: ["marke", "markt", "sichtbarkeit", "referenz", "reputation", "signal", "case", "lernen"]
  }
  ],
  en: [
    {
      name: "Economic Contribution",
      description: "Assesses whether a customer creates measurable economic contribution, margin and predictable revenue.",
      criterionType: "positive",
      criterionKind: "system_metric",
      scoringType: "numeric_higher_better",
      unit: "€",
      keywords: ["revenue", "margin", "contribution", "profit", "profitability", "budget", "payment", "economic", "clv", "umsatz", "marge", "deckungsbeitrag", "zahlung"]
    },
    {
      name: "Strategic Fit",
      description: "Assesses whether a customer fits the positioning, target-customer logic and long-term development.",
      criterionType: "positive",
      criterionKind: "perspective",
      scoringType: "manual_1_10",
      keywords: ["strategy", "fit", "target customer", "industry", "positioning", "growth", "reference", "strategie", "passung", "zielkunde", "branche"]
    },
    {
      name: "Relationship Quality",
      description: "Assesses trust, collaboration, reliability and clarity in the customer relationship.",
      criterionType: "positive",
      criterionKind: "perspective",
      scoringType: "manual_1_10",
      keywords: ["trust", "collaboration", "relationship", "reliable", "communication", "partnership", "loyal", "vertrauen", "zusammenarbeit", "beziehung"]
    },
    {
      name: "Operational Effort",
      description: "Makes visible whether support, exceptions and coordination effort bind more capacity than the customer value justifies.",
      criterionType: "negative",
      criterionKind: "system_metric",
      scoringType: "negative_effort",
      unit: "Hours",
      keywords: ["effort", "exception", "complex", "complaint", "support", "service", "resource", "coordination", "aufwand", "sonderwunsch", "komplex"]
    },
    {
      name: "Decision Clarity",
      description: "Assesses whether decision makers, priorities, budget and next steps are clear on the customer side.",
      criterionType: "positive",
      criterionKind: "perspective",
      scoringType: "manual_1_10",
      keywords: ["decision", "decision maker", "priority", "clarity", "process", "approval", "budget clarity", "entscheidung", "entscheider", "priorität", "klarheit"]
    },
    {
      name: "Recurring Potential",
      description: "Assesses whether need, usage and development are sustainable beyond a single transaction.",
      criterionType: "positive",
      criterionKind: "perspective",
      scoringType: "manual_1_10",
      keywords: ["recurring", "retention", "term", "potential", "expansion", "repeat", "scalable", "wiederkehr", "bindung", "laufzeit", "potenzial"]
    },
    {
      name: "Market and Brand Impact",
      description: "Assesses whether a customer creates visibility, credibility or market learning.",
      criterionType: "positive",
      criterionKind: "perspective",
      scoringType: "manual_1_10",
      keywords: ["brand", "market", "visibility", "reference", "reputation", "signal", "case", "learning", "marke", "markt", "sichtbarkeit", "referenz"]
    }
  ]
};

const perspectiveTypeLabels: Record<WorkspaceLanguage, Record<PerspectiveType, string>> = {
  de: {
    department: "Abteilung",
    executive: "Geschäftsleitung",
    finance: "Finance / Controlling",
    commercial: "Commercial / Revenue",
    operations: "Operations / Delivery",
    other: "Weitere Perspektive"
  },
  en: {
    department: "Department",
    executive: "Executive Management",
    finance: "Finance / Controlling",
    commercial: "Commercial / Revenue",
    operations: "Operations / Delivery",
    other: "Additional Perspective"
  }
};

const systemPerspectiveNames: Record<PerspectiveType, string[]> = {
  department: ["department", "abteilung"],
  executive: ["executive", "executive leadership", "executive perspective", "geschaftsleitung", "geschaftsleitungsperspektive"],
  finance: ["finance", "finance / controlling", "controlling"],
  commercial: ["commercial", "commercial / revenue", "revenue"],
  operations: ["operations", "operations / delivery", "delivery / operations", "delivery"],
  other: ["other", "additional perspective", "weitere perspektive"]
};

const sourceTermTranslations: Record<WorkspaceLanguage, Record<string, string>> = {
  de: {
    revenue: "Umsatz",
    margin: "Marge",
    contribution: "Deckungsbeitrag",
    profit: "Profitabilität",
    profitability: "Profitabilität",
    payment: "Zahlungsqualität",
    strategy: "Strategie",
    "target customer": "Zielkunde",
    trust: "Vertrauen",
    collaboration: "Zusammenarbeit",
    relationship: "Beziehung",
    effort: "Aufwand",
    exception: "Sonderwunsch",
    decision: "Entscheidung",
    "decision maker": "Entscheider",
    priority: "Priorität",
    clarity: "Klarheit",
    recurring: "Wiederkehrendes Potenzial",
    retention: "Bindung",
    brand: "Marke",
    market: "Markt",
    visibility: "Sichtbarkeit"
  },
  en: {
    umsatz: "revenue",
    marge: "margin",
    deckungsbeitrag: "contribution margin",
    profitabil: "profitability",
    zahlung: "payment quality",
    wirtschaft: "economic contribution",
    strategie: "strategy",
    passung: "fit",
    zielkunde: "target customer",
    branche: "industry",
    vertrauen: "trust",
    zusammenarbeit: "collaboration",
    beziehung: "relationship",
    verlasslich: "reliability",
    kommunikation: "communication",
    partnerschaft: "partnership",
    aufwand: "effort",
    sonderwunsch: "exceptions",
    komplex: "complexity",
    betreuung: "support effort",
    entscheidung: "decision clarity",
    entscheider: "decision maker",
    prioritat: "priority",
    klarheit: "clarity",
    wiederkehr: "recurring potential",
    bindung: "retention",
    laufzeit: "contract term",
    potenzial: "potential",
    marke: "brand",
    markt: "market",
    sichtbarkeit: "visibility",
    referenz: "reference value"
  }
};

function normalizeText(value: string): string {
  return value.toLowerCase().normalize("NFKD").replace(/\p{Diacritic}/gu, "");
}

function distributeWholeWeights(count: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor(100 / count);
  const remainder = 100 - base * count;
  return Array.from({ length: count }, (_, index) => base + (index < remainder ? 1 : 0));
}

function collectMatchedTerms(rawInput: string, keywords: string[]): string[] {
  const normalizedInput = normalizeText(rawInput);
  return keywords.filter((keyword) => normalizedInput.includes(normalizeText(keyword))).slice(0, 6);
}

function localizeSourceTerms(terms: string[], language: WorkspaceLanguage): string[] {
  const translations = sourceTermTranslations[language];
  return terms.map((term) => translations[normalizeText(term)] ?? term);
}

export function clusterValueInputs(inputs: DepartmentValueInputForClustering[]): SuggestedCustomerValueCluster[] {
  return clusterValueInputsForLanguage(inputs, "de");
}

export function clusterValueInputsForLanguage(
  inputs: DepartmentValueInputForClustering[],
  language: WorkspaceLanguage = "de"
): SuggestedCustomerValueCluster[] {
  const submittedInputs = inputs.filter((input) => input.rawInput.trim());
  if (submittedInputs.length === 0) return [];

  const clusterDefinitions = clusterDefinitionsByLanguage[language];
  const scored = clusterDefinitions
    .map((definition) => {
      const sourceDepartments = new Set<string>();
      const sourceTerms = new Set<string>();

      for (const input of submittedInputs) {
        const matchedTerms = collectMatchedTerms(input.rawInput, definition.keywords);
        if (matchedTerms.length > 0) {
          sourceDepartments.add(input.departmentName);
          matchedTerms.forEach((term) => sourceTerms.add(term));
        }
      }

      return {
        definition,
        score: sourceTerms.size + sourceDepartments.size,
        sourceTerms: Array.from(sourceTerms),
        sourceDepartments: Array.from(sourceDepartments)
      };
    })
    .sort((left, right) => right.score - left.score);

  const selected = scored.filter((entry) => entry.score > 0).slice(0, 7);
  const fallback = scored.filter((entry) => !selected.includes(entry)).slice(0, Math.max(0, 5 - selected.length));
  const clusters = [...selected, ...fallback].slice(0, 7);
  const weights = distributeWholeWeights(clusters.length);

  return clusters.map((entry, index) => ({
    name: entry.definition.name,
    description: entry.definition.description,
    criterionType: entry.definition.criterionType,
    criterionKind: entry.definition.criterionKind,
    scoringType: entry.definition.scoringType,
    unit: entry.definition.unit,
    sourceTerms: localizeSourceTerms(entry.sourceTerms, language),
    sourceDepartments: entry.sourceDepartments.length ? entry.sourceDepartments : submittedInputs.map((input) => input.departmentName).slice(0, 3),
    suggestedWeight: weights[index] ?? 0,
    sortOrder: index + 1
  }));
}

export function getPerspectiveTypeLabel(type: PerspectiveType | undefined, language: WorkspaceLanguage = "de"): string {
  return perspectiveTypeLabels[language][type ?? "department"];
}

export function getPerspectiveDisplayName(
  perspective: { name?: string; perspectiveType?: PerspectiveType },
  language: WorkspaceLanguage = "de"
): string {
  const type = perspective.perspectiveType ?? "department";
  const name = perspective.name?.trim();
  if (!name) return getPerspectiveTypeLabel(type, language);
  const normalizedName = normalizeText(name);
  if (type === "executive" || systemPerspectiveNames[type].includes(normalizedName)) {
    return getPerspectiveTypeLabel(type, language);
  }
  return name;
}

export function getExecutivePerspective<T extends { perspectiveType?: PerspectiveType; responseStatus?: string; valueInputStatus?: DepartmentValueInputStatus }>(
  perspectives: T[]
): T | undefined {
  const executives = perspectives.filter((perspective) => perspective.perspectiveType === "executive");
  return executives.find((perspective) => perspective.responseStatus === "submitted" || perspective.valueInputStatus === "submitted") ?? executives[0];
}

export function hasExecutivePerspective(perspectives: Array<{ perspectiveType?: PerspectiveType }>): boolean {
  return perspectives.some((perspective) => perspective.perspectiveType === "executive");
}

export function canInviteExecutivePerspective(
  perspectives: Array<{ perspectiveType?: PerspectiveType }>,
  ownerPerspective?: { perspectiveType?: PerspectiveType }
): boolean {
  return ownerPerspective?.perspectiveType !== "executive" && !hasExecutivePerspective(perspectives);
}

export function dedupeExecutivePerspectives<T extends { id?: string; perspectiveType?: PerspectiveType; responseStatus?: string; valueInputStatus?: DepartmentValueInputStatus }>(
  perspectives: T[]
): T[] {
  const canonicalExecutive = getExecutivePerspective(perspectives);
  const canonicalExecutiveId = canonicalExecutive?.id;
  let keptExecutive = false;
  return perspectives.filter((perspective) => {
    if (perspective.perspectiveType !== "executive") return true;
    if (canonicalExecutiveId && perspective.id !== canonicalExecutiveId) return false;
    if (!canonicalExecutiveId && keptExecutive) return false;
    keptExecutive = true;
    return true;
  });
}

export function buildSegmentDefinitions(segmentCount: number, language: WorkspaceLanguage = "de"): SegmentDefinition[] {
  const segmentCountValidation = validateSegmentCount(segmentCount);
  if (!segmentCountValidation.ok) {
    throw new Error(segmentCountValidation.errors[0]?.code ?? "invalid_segment_count");
  }
  const connector = language === "en" ? "to" : "bis";
  const width = 10 / segmentCount;
  return Array.from({ length: segmentCount }, (_, index) => {
    const name = language === "en" ? `Customer Value Range ${index + 1}` : `Kundenwertspanne ${index + 1}`;
    const minScore = index === 0 ? 1 : Number((index * width + 0.01).toFixed(2));
    const maxScore = index === segmentCount - 1 ? 10 : Number(((index + 1) * width).toFixed(2));
    return {
      name,
      minScore,
      maxScore,
      description: `${name}: Customer Value Score ${minScore.toFixed(2)} ${connector} ${maxScore.toFixed(2)}`
    };
  });
}

export function isValidSegmentCount(segmentCount: number): boolean {
  return validateSegmentCount(segmentCount).ok;
}

export { MIN_SEGMENT_COUNT, MAX_SEGMENT_COUNT };

export function segmentForScore(score: number, segments: SegmentDefinition[]): SegmentDefinition | undefined {
  return segments.find((segment) => score >= segment.minScore && score <= segment.maxScore);
}

export function requiresNumericScale(scoringType: CustomerValueScoringType): boolean {
  return scoringType !== "manual_1_10";
}

export function validateNumericScale(scoringType: CustomerValueScoringType, scale: NumericScalePoint[], unit?: string): WeightValidationResult {
  if (!requiresNumericScale(scoringType)) {
    return { ok: true, total: 10, remaining: 0, message: "Manuelle Einschätzung benötigt keine Zahlenbereiche." };
  }
  if (!unit?.trim()) {
    return { ok: false, total: scale.length, remaining: 10 - scale.length, message: "Bitte eine Einheit für das Zahlenkriterium setzen." };
  }
  const sorted = [...scale].sort((left, right) => left.scoreValue - right.scoreValue);
  const hasAllScores = sorted.length === 10 && sorted.every((point, index) => point.scoreValue === index + 1);
  if (!hasAllScores) {
    return { ok: false, total: sorted.length, remaining: 10 - sorted.length, message: "Die Punkteskala muss alle Werte von 1 bis 10 abdecken." };
  }
  const invalidRange = sorted.some((point) =>
    typeof point.minValue === "number" &&
    typeof point.maxValue === "number" &&
    point.minValue > point.maxValue
  );
  if (invalidRange) {
    return { ok: false, total: sorted.length, remaining: 0, message: "Mindestwerte dürfen nicht größer als Höchstwerte sein." };
  }
  const ranges = sorted
    .filter((point) => typeof point.minValue === "number" && typeof point.maxValue === "number")
    .map((point) => ({ minValue: point.minValue as number, maxValue: point.maxValue as number }))
    .sort((left, right) => left.minValue - right.minValue);
  for (let index = 1; index < ranges.length; index += 1) {
    const previous = ranges[index - 1];
    const current = ranges[index];
    if (
      current.minValue <= previous.maxValue
    ) {
      return { ok: false, total: sorted.length, remaining: 0, message: "Zahlenbereiche dürfen sich nicht überschneiden." };
    }
  }
  return { ok: true, total: 10, remaining: 0, message: "Punkteskala ist vollständig." };
}

export function buildDefaultNumericScale(input: {
  scoringType: CustomerValueScoringType;
  unit?: string;
}): NumericScalePoint[] {
  const unit = input.unit;
  if (input.scoringType === "negative_effort" || input.scoringType === "numeric_lower_better") {
    return Array.from({ length: 10 }, (_, index) => {
      const scoreValue = 10 - index;
      const minValue = index === 0 ? 0 : index * 3;
      const maxValue = index === 9 ? undefined : index * 3 + 2;
      return {
        scoreValue,
        minValue,
        maxValue,
        unit,
        label: maxValue === undefined ? `${minValue}+ ${unit ?? ""}`.trim() : `${minValue}-${maxValue} ${unit ?? ""}`.trim()
      };
    }).sort((left, right) => left.scoreValue - right.scoreValue);
  }
  return Array.from({ length: 10 }, (_, index) => ({
    scoreValue: index + 1,
    minValue: index * 10,
    maxValue: index === 9 ? undefined : index * 10 + 9,
    unit,
    label: index === 9 ? `${index * 10}+ ${unit ?? ""}`.trim() : `${index * 10}-${index * 10 + 9} ${unit ?? ""}`.trim()
  }));
}

export function normalizeWeightValue(value: WeightInputValue): number {
  if (value === "" || value === null || value === undefined) return 0;
  const parsed = typeof value === "number" ? value : Number(String(value).replace(",", "."));
  if (!Number.isFinite(parsed)) return Number.NaN;
  return parsed;
}

export function normalizeWeightDistribution(weights: Record<string, WeightInputValue>): Record<string, number> {
  return Object.fromEntries(Object.entries(weights).map(([key, value]) => [key, normalizeWeightValue(value)]));
}

export function calculateWeightTotal(weights: Record<string, WeightInputValue>): number {
  return Object.values(normalizeWeightDistribution(weights)).reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0);
}

export function validateWeightDistribution(weights: Record<string, WeightInputValue>, language: "de" | "en" = "de"): WeightValidationResult {
  const normalizedWeights = normalizeWeightDistribution(weights);
  const values = Object.values(normalizedWeights);
  const hasOnlyWholeNumbers = values.every((value) => Number.isFinite(value) && Number.isInteger(value) && value >= 0);
  const total = calculateWeightTotal(normalizedWeights);
  const remaining = 100 - total;
  const messages = language === "en"
    ? {
        wholeNumbers: "Weightings must be positive whole numbers.",
        remaining: "Please distribute a total of 100%.",
        tooMuch: "The weighting exceeds 100%.",
        complete: "100% assigned."
      }
    : {
        wholeNumbers: "Gewichtungen müssen ganze positive Zahlen sein.",
        remaining: "Bitte verteilen Sie insgesamt 100%.",
        tooMuch: "Die Gewichtung überschreitet 100%.",
        complete: "100% verteilt."
      };

  if (!hasOnlyWholeNumbers) {
    return {
      ok: false,
      total,
      remaining,
      message: messages.wholeNumbers
    };
  }

  if (total !== 100) {
    return {
      ok: false,
      total,
      remaining,
      message: remaining > 0 ? messages.remaining : messages.tooMuch
    };
  }

  return {
    ok: true,
    total,
    remaining: 0,
    message: messages.complete
  };
}

export interface PerspectiveCoverageDepartment {
  name: string;
  perspectiveType?: PerspectiveType;
  isVotingPerspective?: boolean;
  responseStatus?: string;
  valueInputStatus?: DepartmentValueInputStatus;
}

export interface PerspectiveCoverage {
  submittedPerspectives: PerspectiveCoverageDepartment[];
  openPerspectives: PerspectiveCoverageDepartment[];
  recommendedPerspectiveTypes: PerspectiveType[];
  missingExecutive: boolean;
  missingFinanceOrCommercial: boolean;
}

const recommendedPerspectiveTypes: PerspectiveType[] = [
  "executive",
  "finance",
  "commercial",
  "operations",
  "department"
];

export function analyzePerspectiveCoverage(departments: PerspectiveCoverageDepartment[]): PerspectiveCoverage {
  const votingDepartments = departments.filter((department) => department.isVotingPerspective !== false);
  const submittedPerspectives = votingDepartments.filter(
    (department) => department.responseStatus === "submitted" || department.valueInputStatus === "submitted"
  );
  const openPerspectives = votingDepartments.filter((department) => !submittedPerspectives.includes(department));
  const submittedTypes = new Set(submittedPerspectives.map((department) => department.perspectiveType ?? "department"));
  return {
    submittedPerspectives,
    openPerspectives,
    recommendedPerspectiveTypes,
    missingExecutive: !submittedTypes.has("executive"),
    missingFinanceOrCommercial: !submittedTypes.has("finance") && !submittedTypes.has("commercial")
  };
}

export function isBillablePerspective(input: {
  perspectiveType?: PerspectiveType;
  isVotingPerspective?: boolean;
}): boolean {
  if (input.isVotingPerspective === false) return false;
  return (input.perspectiveType ?? "department") === "department";
}

export function valueRoundIsOpen(status?: WorkspaceProcessStatus): boolean {
  return status === "value_round_open";
}

export function valueRoundCanAcceptInput(status?: WorkspaceProcessStatus): boolean {
  return (
    status === "hauffe_os_evaluation_ready" ||
    status === "value_round_open" ||
    status === "value_round_completed" ||
    status === "clusters_ready_for_owner_review" ||
    status === "weighting_open" ||
    status === "calculation_ready_for_owner_review"
  );
}

export function canPrepareClusters(status?: WorkspaceProcessStatus): boolean {
  return status === "value_round_completed" || status === "clusters_ready_for_owner_review";
}

export function canWeightClusters(status?: WorkspaceProcessStatus): boolean {
  return status === "clusters_ready_for_owner_review" || status === "weighting_open" || status === "calculation_ready_for_owner_review";
}

export function customerValueProcessLabel(status?: WorkspaceProcessStatus, language: "de" | "en" = "de"): string {
  const labels: Record<"de" | "en", Record<WorkspaceProcessStatus | "default", string>> = {
    de: {
      department_input_open: "Abteilungsperspektiven offen",
      hauffe_os_evaluation_ready: "HAUFFE OS Auswertung vorhanden",
      value_round_open: "Value-Runde geöffnet",
      value_round_completed: "Value-Runde vollständig",
      clusters_ready_for_owner_review: "Kriterien zur Prüfung bereit",
      weighting_open: "Gewichtung offen",
      calculation_ready_for_owner_review: "Calculation bereit zur Prüfung",
      calculation_confirmed: "Calculation bestätigt",
      default: "Prozess noch nicht gestartet"
    },
    en: {
      department_input_open: "Department perspectives open",
      hauffe_os_evaluation_ready: "HAUFFE OS Evaluation available",
      value_round_open: "Value Round open",
      value_round_completed: "Value Round complete",
      clusters_ready_for_owner_review: "Criteria ready for review",
      weighting_open: "Weighting open",
      calculation_ready_for_owner_review: "Calculation ready for review",
      calculation_confirmed: "Calculation confirmed",
      default: "Process not started yet"
    }
  };
  if (!status) return labels[language].default;
  return labels[language][status];
}
